/**
 * WGS84 ↔ UTM (docs/maengel-verortung-plan.md 9.2, Paket P2).
 *
 * Absteckprotokolle und Lagepläne in Deutschland geben ETRS89/UTM (Zone 32,
 * im Osten 33) an; GPS liefert WGS84. ETRS89 ist auf der Eurasischen Platte
 * festgemacht und driftet gegen WGS84 um ≈ 2,5 cm/Jahr — Stand 2026 knapp
 * 0,9 m. Für Mängel-Pins (GPS ±2–8 m) ist das vernachlässigbar, darum wird hier
 * nicht zwischen beiden unterschieden: dasselbe Ellipsoid (GRS80 ≈ WGS84,
 * Halbachsenunterschied 0,1 mm), dieselben Formeln.
 *
 * Rechenweg: Transversale Mercator-Projektion nach Krüger in der Fassung von
 * Karney (2011, „Transverse Mercator with an accuracy of a few nanometers“,
 * J. Geodesy 85:475–485) mit Reihen bis n⁶ — genau auf wenige nm innerhalb
 * der Zone und auf < 1 mm auch 4–5° neben dem Mittelmeridian (Berlin in
 * Zone 32 erzwungen).
 *
 * Nur Nordhalbkugel: Nordwert ohne 10 000-km-Versatz; südliche Breiten ergeben
 * einen negativen Nordwert. Sonderzonen (Norwegen 32V, Spitzbergen) kennt
 * `utmZoneFuer` nicht — sie betreffen Deutschland nicht.
 *
 * Reine Funktionen, nichts wirft. Nicht endliche Eingaben ergeben `NaN`,
 * Nutzereingaben (`utmEingabeLesen`) Fehler als `{ fehler }` im Klartext.
 */

/** Eine UTM-Koordinate (Nordhalbkugel): Zone 1–60, Ost- und Nordwert in m. */
export interface Utm {
    zone: number;
    ost: number;
    nord: number;
}

// ─── Ellipsoid und Reihenkoeffizienten ──────────────────────────────────────

const A_ELLIPSOID = 6_378_137;
const F_ELLIPSOID = 1 / 298.257223563;
const K0 = 0.9996;
const OST_VERSATZ = 500_000;

const N = F_ELLIPSOID / (2 - F_ELLIPSOID);
const N2 = N * N;
const N3 = N2 * N;
const N4 = N3 * N;
const N5 = N4 * N;
const N6 = N5 * N;
/** Erste Exzentrizität. */
const E = Math.sqrt(F_ELLIPSOID * (2 - F_ELLIPSOID));
const E2 = E * E;
/** Rektifizierender Radius (2πA = Meridianumfang). */
const A_REKT = (A_ELLIPSOID / (1 + N)) * (1 + N2 / 4 + N4 / 64 + N6 / 256);

/** Krüger-Reihe vorwärts (Karney 2011, Gl. 35). */
const ALPHA = [
    N / 2 - (2 * N2) / 3 + (5 * N3) / 16 + (41 * N4) / 180 - (127 * N5) / 288 + (7891 * N6) / 37800,
    (13 * N2) / 48 - (3 * N3) / 5 + (557 * N4) / 1440 + (281 * N5) / 630 - (1983433 * N6) / 1935360,
    (61 * N3) / 240 - (103 * N4) / 140 + (15061 * N5) / 26880 + (167603 * N6) / 181440,
    (49561 * N4) / 161280 - (179 * N5) / 168 + (6601661 * N6) / 7257600,
    (34729 * N5) / 80640 - (3418889 * N6) / 1995840,
    (212378941 * N6) / 319334400,
];

/** Krüger-Reihe rückwärts (Karney 2011, Gl. 36). */
const BETA = [
    N / 2 - (2 * N2) / 3 + (37 * N3) / 96 - N4 / 360 - (81 * N5) / 512 + (96199 * N6) / 604800,
    N2 / 48 + N3 / 15 - (437 * N4) / 1440 + (46 * N5) / 105 - (1118711 * N6) / 3870720,
    (17 * N3) / 480 - (37 * N4) / 840 - (209 * N5) / 4480 + (5569 * N6) / 90720,
    (4397 * N4) / 161280 - (11 * N5) / 504 - (830251 * N6) / 7257600,
    (4583 * N5) / 161280 - (108847 * N6) / 3991680,
    (20648693 * N6) / 638668800,
];

const GRAD = Math.PI / 180;

/** Winkel in Grad auf [−180, 180). */
function laengeNormalisieren(grad: number): number {
    return ((((grad + 180) % 360) + 360) % 360) - 180;
}

function zoneGueltig(zone: unknown): zone is number {
    return typeof zone === 'number' && Number.isInteger(zone) && zone >= 1 && zone <= 60;
}

function mittelmeridianGrad(zone: number): number {
    return (zone - 1) * 6 - 180 + 3;
}

/** τ′ aus τ = tan φ (konforme Breite, Karney 2011 Gl. 7–9). */
function tauStrich(tau: number): number {
    const sigma = Math.sinh(E * Math.atanh((E * tau) / Math.sqrt(1 + tau * tau)));
    return tau * Math.sqrt(1 + sigma * sigma) - sigma * Math.sqrt(1 + tau * tau);
}

/** Vorwärtsrechnung: x/y ab Mittelmeridian bzw. Äquator (m) und Meridiankonvergenz (Grad). */
function vorwaerts(lat: number, lng: number, zone: number): { x: number; y: number; konvergenzGrad: number } {
    const phi = lat * GRAD;
    const lambda = laengeNormalisieren(lng - mittelmeridianGrad(zone)) * GRAD;
    const cosL = Math.cos(lambda);
    const sinL = Math.sin(lambda);
    const tauP = tauStrich(Math.tan(phi));

    const xiP = Math.atan2(tauP, cosL);
    const etaP = Math.asinh(sinL / Math.sqrt(tauP * tauP + cosL * cosL));

    let xi = xiP;
    let eta = etaP;
    let pP = 1;
    let qP = 0;
    for (let j = 1; j <= 6; j++) {
        const a = ALPHA[j - 1];
        const s = Math.sin(2 * j * xiP);
        const c = Math.cos(2 * j * xiP);
        const sh = Math.sinh(2 * j * etaP);
        const ch = Math.cosh(2 * j * etaP);
        xi += a * s * ch;
        eta += a * c * sh;
        pP += 2 * j * a * c * ch;
        qP += 2 * j * a * s * sh;
    }

    // Konvergenz: Anteil der Kugel (γ′) plus Korrektur der Reihe (γ″), Karney Gl. 11/13.
    const gammaP = Math.atan((tauP / Math.sqrt(1 + tauP * tauP)) * Math.tan(lambda));
    const gammaPP = Math.atan2(qP, pP);

    return { x: K0 * A_REKT * eta, y: K0 * A_REKT * xi, konvergenzGrad: (gammaP + gammaPP) / GRAD };
}

// ─── Öffentliche Umrechnungen ───────────────────────────────────────────────

/** Standard-UTM-Zone (6°-Streifen) für eine Länge; Deutschland: 32 (West), 33 (Ost ab 12° O). */
export function utmZoneFuer(lng: number): number {
    if (!Number.isFinite(lng)) return Number.NaN;
    return Math.floor((laengeNormalisieren(lng) + 180) / 6) + 1;
}

/**
 * WGS84 (Grad) → UTM. `zone` erzwingt einen Streifen (Absteckprotokoll in 32
 * auch östlich von 12°); ungültig oder weggelassen → Standardzone der Länge.
 */
export function wgs84ZuUtm(lat: number, lng: number, zone?: number): Utm {
    const z = zoneGueltig(zone) ? zone : utmZoneFuer(lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !zoneGueltig(z)) {
        return { zone: z, ost: Number.NaN, nord: Number.NaN };
    }
    const { x, y } = vorwaerts(lat, lng, z);
    return { zone: z, ost: x + OST_VERSATZ, nord: y };
}

/** UTM → WGS84 (Grad). Nordhalbkugel; ungültige Werte → NaN. */
export function utmZuWgs84(u: Utm): { lat: number; lng: number } {
    if (!u || !zoneGueltig(u.zone) || !Number.isFinite(u.ost) || !Number.isFinite(u.nord)) {
        return { lat: Number.NaN, lng: Number.NaN };
    }
    const eta = (u.ost - OST_VERSATZ) / (K0 * A_REKT);
    const xi = u.nord / (K0 * A_REKT);

    let xiP = xi;
    let etaP = eta;
    for (let j = 1; j <= 6; j++) {
        const b = BETA[j - 1];
        xiP -= b * Math.sin(2 * j * xi) * Math.cosh(2 * j * eta);
        etaP -= b * Math.cos(2 * j * xi) * Math.sinh(2 * j * eta);
    }

    const sinhEtaP = Math.sinh(etaP);
    const sinXiP = Math.sin(xiP);
    const cosXiP = Math.cos(xiP);
    const tauP = sinXiP / Math.sqrt(sinhEtaP * sinhEtaP + cosXiP * cosXiP);

    // τ aus τ′ per Newton (Karney Gl. 19–21); konvergiert in 2–3 Schritten.
    let tau = tauP;
    for (let i = 0; i < 10; i++) {
        const tauIP = tauStrich(tau);
        const delta =
            ((tauP - tauIP) / Math.sqrt(1 + tauIP * tauIP)) *
            ((1 + (1 - E2) * tau * tau) / ((1 - E2) * Math.sqrt(1 + tau * tau)));
        tau += delta;
        if (Math.abs(delta) < 1e-14) break;
    }

    const lat = Math.atan(tau) / GRAD;
    const lng = laengeNormalisieren(mittelmeridianGrad(u.zone) + Math.atan2(sinhEtaP, cosXiP) / GRAD);
    return { lat, lng };
}

/**
 * Meridiankonvergenz: Winkel von geografisch Nord nach Gitternord in Grad,
 * positiv östlich des Mittelmeridians (Nordhalbkugel). Genau nach Karney, nicht
 * nur γ ≈ atan(tan(λ−λ0)·sin φ). Umrechnung: Gitterazimut = wahrer Azimut − γ.
 */
export function meridiankonvergenzGrad(lat: number, lng: number, zone: number): number {
    const z = zoneGueltig(zone) ? zone : utmZoneFuer(lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !zoneGueltig(z)) return Number.NaN;
    return vorwaerts(lat, lng, z).konvergenzGrad;
}

// ─── Formatieren ────────────────────────────────────────────────────────────

function meterFormatieren(wert: number, stellen: number): string {
    if (!Number.isFinite(wert)) return '?';
    const fest = Math.abs(wert).toFixed(stellen);
    const [ganz, bruch] = fest.split('.');
    const gruppiert = ganz.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const vorzeichen = wert < 0 && Number(fest) !== 0 ? '-' : '';
    return vorzeichen + gruppiert + (bruch ? `,${bruch}` : '');
}

/**
 * „32 565 123,45 E · 5 934 567,89 N“ — deutsches Dezimalkomma, Tausender mit
 * Leerzeichen, 0–6 Nachkommastellen (Standard 2). Ohne Band-Buchstaben: der
 * bräuchte die Breite. `utmEingabeLesen` liest das Ergebnis wieder ein.
 */
export function utmFormatieren(u: Utm, nachkommastellen: number = 2): string {
    const stellen = Number.isFinite(nachkommastellen) ? Math.min(6, Math.max(0, Math.round(nachkommastellen))) : 2;
    const zone = zoneGueltig(u?.zone) ? String(u.zone) : '?';
    return `${zone} ${meterFormatieren(u?.ost, stellen)} E · ${meterFormatieren(u?.nord, stellen)} N`;
}

// ─── Eingabe lesen ──────────────────────────────────────────────────────────

const OST_MIN = 100_000;
const OST_MAX = 900_000;
const NORD_MIN = 0;
const NORD_MAX = 9_400_000;
/** Obergrenze der Ziffernblöcke einer Eingabe (schützt vor Einfügen ganzer Listen). */
const MAX_BLOECKE = 12;

/** Welches Zeichen in einer Zahl das Dezimalzeichen ist; `null` = Ganzzahl ohne Trenner. */
type Dezimalzeichen = 'komma' | 'punkt' | null;

interface ZahlDeutung {
    wert: number;
    /** Ziffern vor dem Dezimalzeichen ohne Tausendertrenner (für die vorangestellte Zone). */
    ganz: string;
    /** Ziffern nach dem Dezimalzeichen. */
    bruch: string;
    dezimal: Dezimalzeichen;
}

function deutung(ganz: string, bruch: string, dezimal: Dezimalzeichen): ZahlDeutung {
    return { wert: Number(bruch ? `${ganz}.${bruch}` : ganz), ganz, bruch, dezimal };
}

/** Tausendergruppen gültig? Erste Gruppe 1–3 Ziffern, jede weitere genau 3. */
function tausenderGueltig(gruppen: readonly string[]): boolean {
    return gruppen.length >= 2 && /^\d{1,3}$/.test(gruppen[0]) && gruppen.slice(1).every((g) => /^\d{3}$/.test(g));
}

/**
 * Alle Lesarten einer Zahl aus Ziffern, „.“ und „,“: deutsch (Komma dezimal,
 * Punkt Tausender) und englisch (umgekehrt). „565.123“ hat zwei Lesarten
 * (565,123 oder 565 123) — welche gilt, entscheidet die Plausibilität.
 */
function zahlDeutungen(text: string): ZahlDeutung[] {
    if (!/^\d[\d.,]*$/.test(text) || /[.,]$/.test(text)) return [];
    const punkte = (text.match(/\./g) ?? []).length;
    const kommas = (text.match(/,/g) ?? []).length;
    if (punkte === 0 && kommas === 0) return [deutung(text, '', null)];

    if (punkte > 0 && kommas > 0) {
        // Das letzte Zeichen ist das Dezimalzeichen, das andere der Tausendertrenner.
        const dez = text.lastIndexOf(',') > text.lastIndexOf('.') ? ',' : '.';
        const tausender = dez === ',' ? '.' : ',';
        const i = text.lastIndexOf(dez);
        const vorne = text.slice(0, i);
        const bruch = text.slice(i + 1);
        if (vorne.includes(dez) || bruch.includes(tausender)) return [];
        const gruppen = vorne.split(tausender);
        if (!tausenderGueltig(gruppen)) return [];
        return [deutung(gruppen.join(''), bruch, dez === ',' ? 'komma' : 'punkt')];
    }

    const zeichen = punkte > 0 ? '.' : ',';
    const teile = text.split(zeichen);
    // Als Tausendertrenner gelesen: deutsch bei Punkt, englisch bei Komma.
    const alsTausender: Dezimalzeichen = zeichen === '.' ? 'komma' : 'punkt';
    const alsDezimal: Dezimalzeichen = zeichen === '.' ? 'punkt' : 'komma';
    const ergebnis: ZahlDeutung[] = [];
    if (teile.length === 2) ergebnis.push(deutung(teile[0], teile[1], alsDezimal));
    if (tausenderGueltig(teile)) ergebnis.push(deutung(teile.join(''), '', alsTausender));
    return ergebnis;
}

/**
 * Ziffern-Tokens eines Felds zu Zahlen zusammensetzen: Leerzeichen können
 * Tausendertrenner sein („5 934 567,89“) oder Zahlen trennen. Liefert jede
 * mögliche Folge mit höchstens drei Zahlen.
 */
function gruppierungen(felder: readonly string[][]): string[][] {
    const flach: { text: string; feld: number }[] = [];
    felder.forEach((f, i) => f.forEach((text) => flach.push({ text, feld: i })));
    const ergebnis: string[][] = [];
    const weiter = (i: number, bisher: string[]): void => {
        if (bisher.length > 3) return;
        if (i === flach.length) {
            ergebnis.push(bisher);
            return;
        }
        let text = flach[i].text;
        weiter(i + 1, [...bisher, text]);
        if (!/^\d{1,3}$/.test(text)) return;
        for (let j = i + 1; j < flach.length && flach[j].feld === flach[i].feld; j++) {
            const t = flach[j].text;
            if (/^\d{3}$/.test(t)) {
                text += t;
                weiter(j + 1, [...bisher, text]);
                continue;
            }
            if (/^\d{3}[.,]\d+$/.test(t)) weiter(j + 1, [...bisher, text + t]);
            break;
        }
    };
    weiter(0, []);
    return ergebnis;
}

/** Kartesisches Produkt der Lesarten je Zahl. */
function kombinationen(listen: readonly ZahlDeutung[][]): ZahlDeutung[][] {
    return listen.reduce<ZahlDeutung[][]>(
        (acc, liste) => acc.flatMap((vorne) => liste.map((z) => [...vorne, z])),
        [[]],
    );
}

type Reihenfolge = 'ost-nord' | 'nord-ost';

interface Kandidat {
    utm: Utm;
    /** Kleiner = wahrscheinlicher: +1 vertauschte Reihenfolge ohne Beschriftung, +2 gemischte Zahlformate. */
    rang: number;
}

interface Befund {
    kandidaten: Kandidat[];
    zahlenAnzahl: number | null;
    zonenKonflikt: string | null;
    ungueltigeZone: number | null;
}

/**
 * Ostwert samt Zone: direkt 100 000–900 000 oder mit vorangestellter Zone
 * („32565123,45“). Die Zone als Präfix nur, wenn der Wert an Ost-Stelle steht
 * (vorn oder beschriftet) — sonst würde aus einem vertauschten, zu großen
 * Nordwert eine erfundene Zone 1–9.
 */
function ostMitZone(z: ZahlDeutung, praefixErlaubt: boolean): { ost: number; zone: number | null } | null {
    if (z.wert >= OST_MIN && z.wert <= OST_MAX) return { ost: z.wert, zone: null };
    if (praefixErlaubt && (z.ganz.length === 7 || z.ganz.length === 8)) {
        const zone = Number(z.ganz.slice(0, z.ganz.length - 6));
        const rest = Number(z.bruch ? `${z.ganz.slice(-6)}.${z.bruch}` : z.ganz.slice(-6));
        if (zoneGueltig(zone) && rest >= OST_MIN && rest <= OST_MAX) return { ost: rest, zone };
    }
    return null;
}

const BUCHSTABE = 'A-Za-zÄÖÜäöüß';
const ZONE_MIT_BAND = new RegExp(`(?<![\\d.,])(\\d{1,2})([C-HJ-NP-Xc-hj-np-x])(?![${BUCHSTABE}])`, 'g');
const ZONE_WORT = /\b(?:UTM|Zone)\s*(?:zone\s*)?(\d{1,2})(?!\d)\s*(?:[C-HJ-NP-X]|north|nord)?(?![A-Za-zÄÖÜäöüß])/gi;
const EPSG = /\bEPSG\s*:?\s*258(\d{2})\b/gi;
const BEZUG = /\b(?:ETRS\s*89|WGS\s*84|GRS\s*80)\b/gi;
const EINHEIT = new RegExp(`(?<![${BUCHSTABE}])m(?![${BUCHSTABE}])`, 'gi');
const OST_WORT = 'ostwert|easting|rechtswert|east|rechts|ost|rw|e|o|r';
const NORD_WORT = 'nordwert|northing|hochwert|north|nord|hoch|hw|n|h';
const BESCHRIFTUNG = new RegExp(`(?<![${BUCHSTABE}])(${OST_WORT}|${NORD_WORT})(?![${BUCHSTABE}])\\s*[:=]?`, 'gi');
const NORD_WORT_GENAU = new RegExp(`^(?:${NORD_WORT})$`, 'i');

function analysieren(text: string, kommaTrennt: boolean, standardZone: number): Befund | { fehler: string } {
    let rest = text;
    const zonen: number[] = [];
    const ungueltig: number[] = [];
    const merkeZone = (roh: string): string => {
        const z = Number(roh);
        if (zoneGueltig(z)) zonen.push(z);
        else ungueltig.push(z);
        return ' ';
    };

    rest = rest.replace(EPSG, (_m, z: string) => merkeZone(z));
    rest = rest.replace(BEZUG, ' ');
    rest = rest.replace(ZONE_WORT, (_m, z: string) => merkeZone(z));
    rest = rest.replace(ZONE_MIT_BAND, (_m, z: string) => merkeZone(z));
    rest = rest.replace(EINHEIT, ' ');

    // Beschriftungen: nur wenn Ost UND Nord beschriftet sind, legen sie die Reihenfolge fest.
    const beschriftet: { nord: boolean; pos: number }[] = [];
    rest = rest.replace(BESCHRIFTUNG, (_m, wort: string, pos: number) => {
        beschriftet.push({ nord: NORD_WORT_GENAU.test(wort), pos });
        return '|';
    });
    const ersteOst = beschriftet.find((b) => !b.nord);
    const ersteNord = beschriftet.find((b) => b.nord);
    const reihenfolge: Reihenfolge | null =
        ersteOst && ersteNord ? (ersteOst.pos < ersteNord.pos ? 'ost-nord' : 'nord-ost') : null;

    rest = rest.replace(/[;/|·•\t\r\n\u2013\u2014-]/g, '|');
    rest = kommaTrennt ? rest.replace(/,/g, '|') : rest.replace(/,(?=\s|$)/g, '|');

    const fremd = rest.match(/[^\d.,\s|]+/g);
    if (fremd) return { fehler: `Nicht erkannt: „${fremd.join(' ')}“ — erwartet z. B. „32U 565123,45 5934567,89“.` };

    const zoneText = zonen.length > 0 ? zonen[0] : null;
    if (zonen.some((z) => z !== zoneText)) {
        return { fehler: `Zone widersprüchlich: ${[...new Set(zonen)].join(' und ')}.` };
    }

    const felder = rest
        .split('|')
        .map((f) => f.trim().split(/\s+/).filter(Boolean))
        .filter((f) => f.length > 0);

    const befund: Befund = {
        kandidaten: [],
        zahlenAnzahl: null,
        zonenKonflikt: null,
        ungueltigeZone: ungueltig.length > 0 ? ungueltig[0] : null,
    };
    // Mehr als 12 Ziffernblöcke (drei Zahlen mit je vier Tausendergruppen) ergeben
    // nie eine Koordinate — nicht erst alle Gruppierungen durchprobieren.
    const bloecke = felder.flat().length;
    if (bloecke > MAX_BLOECKE) {
        befund.zahlenAnzahl = bloecke;
        return befund;
    }
    const folgen = gruppierungen(felder);
    // Mehr als drei Zahlen verwirft `gruppierungen` schon unterwegs — dann zählt die Rohzahl.
    befund.zahlenAnzahl = folgen.length > 0 ? Math.min(...folgen.map((f) => f.length)) : bloecke;
    for (const folge of folgen) {
        if (folge.length !== 2 && folge.length !== 3) continue;
        for (const zahlen of kombinationen(folge.map(zahlDeutungen))) {
            let zoneVorne: number | null = null;
            let paar = zahlen;
            if (zahlen.length === 3) {
                const erste = zahlen[0];
                if (erste.dezimal !== null || !/^\d{1,2}$/.test(erste.ganz)) continue;
                if (!zoneGueltig(erste.wert)) {
                    befund.ungueltigeZone = erste.wert;
                    continue;
                }
                zoneVorne = erste.wert;
                paar = zahlen.slice(1);
            }
            const formate = new Set(paar.map((z) => z.dezimal).filter((d) => d !== null));
            const gemischt = formate.size > 1 ? 2 : 0;
            const reihen: Reihenfolge[] = reihenfolge ? [reihenfolge] : ['ost-nord', 'nord-ost'];
            for (const r of reihen) {
                const [ostZ, nordZ] = r === 'ost-nord' ? [paar[0], paar[1]] : [paar[1], paar[0]];
                const ost = ostMitZone(ostZ, r === 'ost-nord' || reihenfolge !== null);
                if (!ost || nordZ.wert < NORD_MIN || nordZ.wert > NORD_MAX) continue;
                const angaben = [zoneText, zoneVorne, ost.zone].filter((z): z is number => z !== null);
                if (angaben.some((z) => z !== angaben[0])) {
                    befund.zonenKonflikt = `Zone widersprüchlich: ${[...new Set(angaben)].join(' und ')}.`;
                    continue;
                }
                const zone = angaben.length > 0 ? angaben[0] : standardZone;
                const vertauscht = !reihenfolge && r === 'nord-ost' ? 1 : 0;
                befund.kandidaten.push({ utm: { zone, ost: ost.ost, nord: nordZ.wert }, rang: vertauscht + gemischt });
            }
        }
    }
    return befund;
}

function gleich(x: Utm, y: Utm): boolean {
    return x.zone === y.zone && Math.abs(x.ost - y.ost) < 1e-6 && Math.abs(x.nord - y.nord) < 1e-6;
}

/**
 * Liest UTM-Koordinaten aus Freitext:
 * „32U 565123.45 5934567.89“, „32 565123,45 5934567,89“,
 * „32565123.45 5934567.89“ (Zone als Präfix am Ostwert),
 * „E 565123,45 N 5934567,89“ (auch „N … E …“, „Ost/Nord“, „RW/HW“),
 * „565.123,45 / 5.934.567,89“, „565123.45;5934567.89“ (dann `standardZone`),
 * „UTM 32N …“, „EPSG:25832 …“ und die Ausgabe von `utmFormatieren`.
 * Deutsche und englische Zahlformate, Leerzeichen als Tausendertrenner.
 * Mehrdeutiges wird über die Plausibilität (Ost 100 000–900 000,
 * Nord 0–9 400 000) aufgelöst; ohne Beschriftung gilt „Ost vor Nord“, eine
 * vertauschte Eingabe wird erkannt, wenn nur sie plausibel ist. Bleibt es
 * mehrdeutig, kommt ein Fehler statt eines geratenen Werts.
 */
export function utmEingabeLesen(text: string, standardZone: number = 32): Utm | { fehler: string } {
    const roh = (typeof text === 'string' ? text : '')
        .replace(/[\u00a0\u202f\u2009\u2007]/g, ' ')
        .replace(/(?<=\d)['’](?=\d{3})/g, '')
        .trim();
    if (!roh) return { fehler: 'Keine Koordinaten eingegeben.' };
    if (!/\d/.test(roh)) return { fehler: 'Keine Zahlen gefunden — erwartet z. B. „32U 565123,45 5934567,89“.' };
    const zoneStandard = zoneGueltig(standardZone) ? standardZone : 32;

    const erster = analysieren(roh, false, zoneStandard);
    if ('fehler' in erster) return erster;
    let befund = erster;
    // Zweiter Anlauf: Komma ohne Leerzeichen als Feldtrenner („565123.45,5934567.89“).
    if (befund.kandidaten.length === 0 && roh.includes(',')) {
        const zweiter = analysieren(roh, true, zoneStandard);
        if (!('fehler' in zweiter) && zweiter.kandidaten.length > 0) befund = zweiter;
    }

    if (befund.kandidaten.length === 0) {
        if (befund.zonenKonflikt) return { fehler: befund.zonenKonflikt };
        if (befund.ungueltigeZone !== null) return { fehler: `Zone ${befund.ungueltigeZone} gibt es nicht (1–60).` };
        const anzahl = befund.zahlenAnzahl ?? 0;
        if (anzahl > 3 || anzahl < 2) {
            return {
                fehler: `Erwartet werden Ostwert und Nordwert, optional mit Zone davor — erkannt: ${anzahl} ${anzahl === 1 ? 'Zahl' : 'Zahlen'}.`,
            };
        }
        return {
            fehler:
                'Werte unplausibel: Ostwert 100 000–900 000 m (oder mit Zone davor, z. B. 32565123,45), Nordwert 0–9 400 000 m. Stellen prüfen.',
        };
    }

    const besterRang = Math.min(...befund.kandidaten.map((k) => k.rang));
    const beste: Utm[] = [];
    for (const k of befund.kandidaten) {
        if (k.rang === besterRang && !beste.some((b) => gleich(b, k.utm))) beste.push(k.utm);
    }
    if (beste.length > 1) {
        const lesarten = beste.slice(0, 3).map((u) => `„${utmFormatieren(u, 3)}“`).join(' oder ');
        return { fehler: `Mehrdeutig: ${lesarten} — bitte mit Dezimalkomma eindeutig eingeben.` };
    }
    return beste[0];
}
